import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@core/components/Button';
import { initiatePhoneCall } from '@core/api/phoneVerification';
import {
  PHONE_CALL_RESEND_SECONDS,
  apiErrorCode,
  formatPhone,
  normalizePhoneInput,
  phoneErrorMessage,
} from '@core/utils/phone';

interface PhoneCodeFormProps {
  /** Номер (E.164) и 4 цифры из звонка; ошибка (ApiError) показывается под полем кода. */
  onConfirm: (phone: string, code: string) => Promise<void>;
  confirmLabel?: string;
}

const inputClass =
  'w-full rounded-brand bg-page border border-border px-4 py-3 text-text-main outline-none focus:border-primary';

/**
 * Подтверждение номера звонком (Plusofon flash-call): номер → звонок → последние 4 цифры звонящего
 * номера. Звонит форма сама (POST /phone-verification/initiate), а код проверяет вызывающий экран
 * в onConfirm — привязка на странице карты или вход «сначала номер» в мини-аппе.
 */
export function PhoneCodeForm({ onConfirm, confirmLabel = 'Подтвердить' }: PhoneCodeFormProps) {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const toCodeStep = (target: string) => {
    setPhone(target);
    setCode('');
    setStep('code');
    setResendIn(PHONE_CALL_RESEND_SECONDS);
  };

  async function requestCall(target: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await initiatePhoneCall(target);
      toCodeStep(target);
    } catch (err) {
      if (apiErrorCode(err) === 'CODE_COOLDOWN') {
        // На этот номер звонили меньше минуты назад — код того звонка ещё действует.
        toCodeStep(target);
        setNotice('Звонок уже был — введите последние 4 цифры номера, с которого звонили.');
      } else {
        setError(phoneErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  function submitPhone() {
    const normalized = normalizePhoneInput(phoneInput);
    if (!normalized) {
      setError('Проверьте номер — например, +7 999 123-45-67.');
      return;
    }
    void requestCall(normalized);
  }

  async function submitCode() {
    if (busy || code.length !== 4) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await onConfirm(phone, code);
    } catch (err) {
      setError(phoneErrorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  const message = error ? (
    <p className="text-sm text-error bg-error/10 border border-error/20 rounded-brand px-3 py-2 text-left">{error}</p>
  ) : notice ? (
    <p className="text-sm text-text-muted bg-secondary/50 rounded-brand px-3 py-2 text-left">{notice}</p>
  ) : null;

  if (step === 'phone') {
    return (
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          submitPhone();
        }}
      >
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+7 999 123-45-67"
          aria-label="Номер телефона"
          value={phoneInput}
          onChange={(e) => {
            setPhoneInput(e.target.value);
            setError(null);
          }}
          className={`${inputClass} text-lg`}
        />
        {message}
        <Button type="submit" fullWidth disabled={busy || !phoneInput.trim()}>
          <span className="inline-flex items-center justify-center gap-2">
            {busy && <Loader2 size={18} className="animate-spin" />}
            {busy ? 'Звоним…' : 'Позвонить мне'}
          </span>
        </Button>
        <p className="text-xs text-text-muted text-center">
          Мы позвоним на этот номер — отвечать не нужно, код — последние 4 цифры звонящего номера.
        </p>
      </form>
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submitCode();
      }}
    >
      <p className="text-sm text-text-muted text-center">
        Сейчас на {formatPhone(phone)} поступит звонок — отвечать не нужно. Введите последние 4 цифры
        номера, с которого вам позвонят.
      </p>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="____"
        aria-label="Последние 4 цифры номера"
        value={code}
        maxLength={4}
        onChange={(e) => {
          setCode(e.target.value.replace(/\D/g, '').slice(0, 4));
          setError(null);
        }}
        className={`${inputClass} text-center text-2xl tracking-[0.5em]`}
      />
      {message}
      <Button type="submit" fullWidth disabled={busy || code.length !== 4}>
        <span className="inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={18} className="animate-spin" />}
          {busy ? 'Проверяем…' : confirmLabel}
        </span>
      </Button>
      <div className="flex items-center justify-between text-xs text-text-muted">
        <button
          type="button"
          className="underline disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            setStep('phone');
            setCode('');
            setError(null);
            setNotice(null);
          }}
        >
          Изменить номер
        </button>
        <button
          type="button"
          className="underline disabled:opacity-50 disabled:no-underline"
          disabled={busy || resendIn > 0}
          onClick={() => void requestCall(phone)}
        >
          {resendIn > 0 ? `Позвонить ещё раз через ${resendIn} с` : 'Позвонить ещё раз'}
        </button>
      </div>
    </form>
  );
}
